import { defineStore } from "pinia";
import { ref } from "vue";
import Api from "@/services/Api";
import Cache from "@/services/Cache";
import Emitter from "@/services/Emitter";
import type { ClientModel } from "@/models/ClientModel";

export const useClientStore = defineStore("client", () => 
{
    const CACHE_KEY = "store_client_data";
    const list = ref<Array<ClientModel>>([]);
    const isInitialized = ref<boolean>(false);
    let initPromise: Promise<void> | null = null;

    const init = async (): Promise<void> => {
        // If already initialized, return immediately
        if (isInitialized.value) return;
        
        // If initialization is in progress, return the existing promise
        if (initPromise) return initPromise;
        
        // Start new initialization
        initPromise = (async () => {
            try {
                // Check cache first
                const cached: Array<ClientModel> | undefined = Cache.get(CACHE_KEY);
                if (cached && Array.isArray(cached) && cached.length > 0) {
                    list.value = cached;
                    isInitialized.value = true;
                    return;
                }
                
                // Load from server if cache is empty
                await reload();
            } finally {
                // Clear the promise when done (success or failure)
                initPromise = null;
            }
        })();
        
        return initPromise;
    };

    const reload = async (): Promise<void> =>
    {
        list.value = await Api.loadClients();
        Cache.set(CACHE_KEY, list.value);
        isInitialized.value = true;
    };

    const refresh = async (): Promise<void> => {
        await reload();
    };

    const getByCustomerNumber = async(cust: number): Promise<ClientModel | null> => 
    {
        await init();

        for (let i = 0; i < list.value.length; i++)
        {
            const row = list.value[i];
            if (row.CustomerNumber === cust)
            {
                return row;
            } 
        }

        return null;
    };

    const getById = async (id: number): Promise<ClientModel | null> =>
    {
        await init();

        for (let i = 0; i < list.value.length; i++)
        {
            const row = list.value[i];
            if (row.Id === id)
            {
                return row;
            } 
        }

        return null;
    };

    // Register refresh event listener
    Emitter.on("refresh:clientStore", async () => {
        console.debug("refresh:clientStore event received");
        await refresh();
    });

    // Auto-initialize on store creation
    init();

    return { list, reload, refresh, getById, getByCustomerNumber }

});
